// @ts-check
/**
 * EdocReferencesView Component (Factory Pattern)
 *
 * Displays ReferenceUnitHandle objects for EDoc-EDoc ReferenceUnit.
 *
 * Key characteristics:
 * - 3-column table: Document Identifier (clickable), Native Type, and Reason
 * - Uses .crv-* CSS classes for consistent table styling with EmailCommunication
 * - Each EDoc is both the search unit and reference unit (1:1 mapping)
 * - search_units always has single entry with hit=true
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */

/**
 * @param {any} deps - Injected Vue/store dependencies, see componentRegistry.js.
 *   Typed as `any` to preserve the factory-pattern escape hatch for
 *   dynamically-loaded configurable types.
 */
export default function createComponent(deps) {
    const {
        computed,
        inject,
        useDataViewStore,
        RawViewContainer,
        ReferenceRowTags,
        useRowSelection,
    } = deps;

    return {
        name: 'EdocReferencesView',

        components: {
            RawViewContainer,
            ReferenceRowTags,
        },

        props: {
            references: {
                type: Array,
                required: true,
            },
            question: {
                type: String,
                required: true,
            },
        },

        /** @param {{ references: ReferenceUnitHandle[], question: string }} props */
        setup(props) {
            const dataViewStore = useDataViewStore();
            const openDetailView = inject('openDetailView', null);

            /** @param {ReferenceUnitHandle} reference */
            const handleDocumentClick = (reference) => {
                console.log('[EdocReferencesView] Opening detail view for:', reference);

                const refIndex = sortedReferences.value.findIndex(
                    /** @param {ReferenceUnitHandle} r */
                    (r) => r.content_hash === reference.content_hash,
                );

                if (refIndex !== -1) {
                    dataViewStore.communicationGroups = sortedReferences.value;
                    dataViewStore.currentGroupIndex = refIndex;

                    if (openDetailView) {
                        openDetailView();
                    } else {
                        dataViewStore.openCommunicationRawView(sortedReferences.value, refIndex);
                    }
                }
            };

            /** @param {ReferenceUnitHandle} ref */
            const isRowSelected = (ref) => {
                const currentRef =
                    dataViewStore.communicationGroups[dataViewStore.currentGroupIndex];
                return currentRef && currentRef.content_hash === ref.content_hash;
            };

            const sortedReferences = computed(() => {
                /** @type {ReferenceUnitHandle[]} */
                const refs = [...props.references];
                refs.sort(
                    /**
                     * @param {ReferenceUnitHandle} a
                     * @param {ReferenceUnitHandle} b
                     */
                    (a, b) => {
                        const idA = /** @type {number} */ (a.metadata?.id) || 0;
                        const idB = /** @type {number} */ (b.metadata?.id) || 0;
                        return idA - idB;
                    },
                );
                return refs;
            });

            // Bulk-selection checkboxes + session-only tag display. Shared
            // behavior lives in useRowSelection / referenceSelectionStore.
            const rowSelection = useRowSelection(() => sortedReferences.value);

            return {
                sortedReferences,
                openDetailView,
                handleDocumentClick,
                isRowSelected,
                selAllChecked: rowSelection.allChecked,
                selSomeChecked: rowSelection.someChecked,
                toggleSelectAll: rowSelection.toggleAll,
                isRowChecked: rowSelection.isChecked,
                toggleRowChecked: rowSelection.toggleChecked,
                showCommunicationRawView: computed(() => dataViewStore.showCommunicationRawView),
                communicationGroups: computed(() => dataViewStore.communicationGroups),
                currentGroupIndex: computed(() => dataViewStore.currentGroupIndex),
                closeCommunicationRawView: () => dataViewStore.closeCommunicationRawView(),
            };
        },

        template: `
            <div class="conversation-references-view edoc-references-view">
                <!-- EDoc Table -->
                <div class="crv-table-container">
                    <table class="crv-table edoc-table">
                        <colgroup>
                            <col class="crv-select-col" />
                            <col />
                            <col style="width: 30%" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        title="Select all"
                                        :checked="selAllChecked"
                                        :indeterminate="selSomeChecked"
                                        @change="toggleSelectAll"
                                    />
                                </th>
                                <th>Document</th>
                                <th>Native Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="ref in sortedReferences"
                                :key="ref.content_hash"
                                :class="{ 'row-selected': isRowSelected(ref) }"
                                @click="handleDocumentClick(ref)"
                            >
                                <td class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        :checked="isRowChecked(ref)"
                                        @change="toggleRowChecked(ref)"
                                    />
                                </td>
                                <td>
                                    <span class="crv-conversation-link">
                                        {{ ref.display_identifier }}
                                    </span>
                                    <ReferenceRowTags :content-hash="ref.content_hash" />
                                </td>
                                <td>{{ ref.metadata?.native_type || '' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty state -->
                <div v-if="sortedReferences.length === 0" class="crv-empty">
                    No document references found.
                </div>

                <!-- Raw View Modal (only if panel not available) -->
                <RawViewContainer
                    v-if="!openDetailView && communicationGroups.length > 0 && showCommunicationRawView"
                    :references="communicationGroups"
                    :initialIndex="currentGroupIndex"
                    :visible="showCommunicationRawView"
                    @close="closeCommunicationRawView"
                />
            </div>
        `,
    };
}
